import React, { useMemo, useState } from "react";

import { faTable } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { isPlainObject } from "lodash";
import { useDebouncedCallback } from "use-debounce";

import { GenericObject } from "src/api/flowTypes";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { DisplayDateTime } from "src/base-components/Date";
import { MultilineDataValue } from "src/base-components/MultilineDataValue";
import { Pill } from "src/base-components/Pill";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import {
  TableBlockMultiColumnRecord,
  TableBlockRecord,
  TableBlockSingleRecord,
} from "src/cases/queries/casesTypes";
import { FloatingWindow } from "src/datasets/DatasetTable/FloatingWindow";
import { JSONValueViewer } from "src/dataTable/DetailedView/JSONValueViewer";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { copyTextToClipboard } from "src/utils/clipboard";
import { isISO8601 } from "src/utils/datetime";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { isProbablyMarkdown } from "src/utils/stringUtils";

type TableBlockData = {
  record: TableBlockRecord;
};

type TableBlockProps = Omit<CaseBlockProps, "children"> & {
  data: TableBlockData;
};

// Component for displaying nested objects/arrays with FloatingWindow
const NestedValueCell: React.FC<{
  value: unknown;
  columnKey: string;
}> = ({ value, columnKey }) => {
  const [hovering, setHovering] = useState(false);

  // Debounced handler for smooth hover interaction
  const debouncedSetHovering = useDebouncedCallback(setHovering, 200);

  const handleMouseEnter = () => {
    debouncedSetHovering(true);
  };

  const handleMouseLeave = () => {
    debouncedSetHovering(false);
  };

  const handleFloatingWindowMouseOver = () => {
    debouncedSetHovering.cancel();
    setHovering(true);
  };

  // Convert to string for preview
  const stringValue = (() => {
    try {
      return JSON.stringify(value);
    } catch {
      return "{}";
    }
  })();

  return (
    <div
      className="min-w-0 flex-auto"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <FloatingWindow
        button={
          <Pill hovered={hovering} size="sm" variant="gray" maxWidth>
            <Pill.Text fontType="code-12">{stringValue.slice(0, 75)}</Pill.Text>
          </Pill>
        }
        draggable={true}
        isOpen={hovering}
        maximizable={true}
        offset={[0, 4]}
        pinnable={true}
        placement="auto"
        title={columnKey}
        onClose={() => setHovering(false)}
        onCopy={() => copyTextToClipboard(stringValue)}
        onMouseLeave={handleMouseLeave}
        onMouseOver={handleFloatingWindowMouseOver}
      >
        <ObjectDetailPane
          innerDimensionClass="h-full"
          jsonObject={value as Record<string, unknown>}
        />
      </FloatingWindow>
    </div>
  );
};

// Component for truncated text with tooltip and copy
const TruncatedText: React.FC<{
  value: string;
  children?: React.ReactNode;
}> = ({ value, children }) => {
  const textContent = children || (
    <div className="truncate text-gray-800">{value}</div>
  );

  return (
    <Tooltip
      delay={200}
      headerAction={
        <CopyTextIcon
          feedback="inline-only-checkmark"
          size="xs"
          value={value}
        />
      }
      placement="top"
      title={value}
      asChild
    >
      {textContent}
    </Tooltip>
  );
};

// Extract union of all first-level keys from array of objects
const getAllKeys = (records: TableBlockMultiColumnRecord): string[] => {
  const keySet = new Set<string>();
  records.forEach((record: TableBlockSingleRecord) => {
    Object.keys(record).forEach((key) => keySet.add(key));
  });
  return Array.from(keySet);
};

// Render value for PropertiesList (single object display)
const renderValue = (value: unknown, key: string): React.ReactNode => {
  if (typeof value === "number") {
    return <span className="font-code-12">{value}</span>;
  }

  if (typeof value === "boolean" || value === null) {
    return speakPythonPrimitive(String(value));
  }

  if (Array.isArray(value) || isPlainObject(value)) {
    return <JSONValueViewer field={key} value={value as GenericObject} />;
  }

  if (typeof value === "string" && isISO8601(value)) {
    return <DisplayDateTime value={value} />;
  }

  if (typeof value === "string" && isProbablyMarkdown(value)) {
    return <MultilineDataValue fieldName={key} value={value} />;
  }

  return String(value);
};

// Render cell value for table display (array of objects)
const renderCellValue = (value: unknown, key: string): React.ReactNode => {
  // Handle undefined/missing values - leave empty
  if (value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return (
      <div className="flex w-full justify-end">
        <TruncatedText value={String(value)}>
          <div className="truncate font-code-12">{value}</div>
        </TruncatedText>
      </div>
    );
  }

  if (typeof value === "boolean") {
    return (
      <Pill size="sm" variant={value ? "green" : "red"}>
        <Pill.Text>{speakPythonPrimitive(String(value))}</Pill.Text>
      </Pill>
    );
  }

  if (value === null) {
    return speakPythonPrimitive(String(value));
  }

  // Nested objects/arrays - show truncated preview with FloatingWindow
  if (isPlainObject(value) || Array.isArray(value)) {
    return <NestedValueCell columnKey={key} value={value} />;
  }

  if (typeof value === "string" && isISO8601(value)) {
    return (
      <Tooltip
        delay={200}
        headerAction={
          <CopyTextIcon
            feedback="inline-only-checkmark"
            size="xs"
            value={value}
          />
        }
        placement="top"
        title={value}
        asChild
      >
        <div>
          <DisplayDateTime value={value} />
        </div>
      </Tooltip>
    );
  }

  // Regular string values - with conditional tooltip for truncated content
  return <TruncatedText value={String(value)} />;
};

// Column helper for dynamic table columns
const helper = createColumnHelper<Record<string, unknown>>();

// Build dynamic columns based on extracted keys
const buildDynamicColumns = (keys: string[]) => {
  return keys.map((key) =>
    helper.accessor((row) => row[key], {
      id: key,
      maxSize: 350,
      header: (info) => (
        <Header dataLoc={`table-block-header-${key}`} info={info}>
          {key}
        </Header>
      ),
      cell: (info) => (
        <Cell
          className="truncate"
          dataLoc={`table-block-cell-${key}`}
          info={info}
        >
          {renderCellValue(info.getValue(), key)}
        </Cell>
      ),
    }),
  );
};

// Single object display using PropertiesList
const SingleRecordDisplay: React.FC<{
  record: TableBlockSingleRecord;
}> = ({ record }) => {
  const items: PropertiesListItem[] = Object.entries(record).map(
    ([key, value]) => ({
      key,
      label: key,
      value: renderValue(value, key),
    }),
  );

  return (
    <PropertiesList
      dataLocPrefix="table-block"
      items={items}
      rightAlignValues={true}
      showIcons={false}
    />
  );
};

// Multi-record display using TableComp
const MultiRecordDisplay: React.FC<{
  records: TableBlockMultiColumnRecord;
}> = ({ records }) => {
  const columns = useMemo(() => {
    const keys = getAllKeys(records);
    return buildDynamicColumns(keys);
  }, [records]);

  return (
    <div className="flex h-full flex-col p-4">
      <div className="min-h-0 flex-1">
        <TableComp
          columns={columns}
          data={records}
          dataLoc="table-block-multi-record"
          disableVirtualization={true}
          frameClassName="h-full"
          getRowId={(_, index) => String(index)}
          rowClassName="hover:bg-gray-50"
          variant="compact"
        />
      </div>
    </div>
  );
};

export const TableBlock: React.FC<TableBlockProps> = ({ data, ...props }) => {
  const isMultiRecord = Array.isArray(data.record);
  const isSingleRecord = !Array.isArray(data.record);

  // Check for empty data
  const isEmpty = isMultiRecord
    ? data.record.length === 0
    : Object.keys(data.record).length === 0;

  if (isEmpty) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          description="This table has no data to display"
          headline="No data available"
          icon={faTable}
          size="sm"
        />
      </CaseBlock>
    );
  }

  // Single object - use PropertiesList
  if (isSingleRecord) {
    return (
      <CaseBlock {...props}>
        <SingleRecordDisplay record={data.record as TableBlockSingleRecord} />
      </CaseBlock>
    );
  }

  // Array of objects - use TableComp
  return (
    <CaseBlock {...props} noPaddings={true} noScroll={true}>
      <MultiRecordDisplay
        records={data.record as TableBlockMultiColumnRecord}
      />
    </CaseBlock>
  );
};
